/**
 * PropertyHero Component
 * 
 * Core component for displaying property hero section with Airbnb-style image gallery.
 * Supports featured content overlays for prominently featured properties.
 */

'use client';

import Image from 'next/image';
import { useState } from 'react';
import type { MediaResponseDto } from '@/lib/api/clients/properties';
import type { FeaturedPlacement, FeaturedPlacementSummary } from '@/lib/api/clients/featured-content';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import type { FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';

export interface PropertyHeroProps {
  title: string;
  primaryImage?: string | null;
  images?: MediaResponseDto[];
  className?: string;
  /** Featured placements for this property */
  featuredPlacements?: (FeaturedPlacement | FeaturedPlacementSummary)[];
  /** Whether to show the featured badge overlay */
  showFeaturedBadge?: boolean;
}

export function PropertyHero({
  title,
  primaryImage,
  images = [],
  className = '',
  featuredPlacements = [],
  showFeaturedBadge = true,
}: PropertyHeroProps) {
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Combine primary image with gallery images
  const allImages = primaryImage 
    ? [{ id: 'primary', url: primaryImage }, ...images]
    : images;
    
  // Get primary featured badge
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  if (allImages.length === 0) {
    return (
      <div className={`relative w-full h-[400px] md:h-[500px] bg-gray-200 flex items-center justify-center ${className}`}>
        <p className="text-gray-500 text-lg">No images available</p>
      </div>
    );
  }

  // Airbnb-style: Show first 5 images in grid layout
  const mainImage = allImages[0]!; // Safe because we checked length above
  const gridImages = allImages.slice(1, 5);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <>
      {/* Airbnb-Style Grid Layout */}
      <div className={`relative w-full ${className}`}>
        {/* Featured Badge Overlay - Top Left */}
        {primaryFeatured?.badgeVariant && (
          <div className="absolute top-4 left-4 z-20">
            <FeaturedBadge
              variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
              label={primaryFeatured.label}
              size="lg"
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[400px] md:h-[500px]">
          {/* Main large image - left side */}
          <div 
            className="md:col-span-2 relative cursor-pointer overflow-hidden group"
            onClick={() => {
              setCurrentImageIndex(0);
              setShowGallery(true);
            }}
          >
            <Image
              src={mainImage.url}
              alt={title}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105"
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
          </div>

          {/* Grid of smaller images - right side */}
          <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-2">
            {gridImages.map((image, index) => (
              <div
                key={image.id}
                className="relative cursor-pointer overflow-hidden group"
                onClick={() => {
                  setCurrentImageIndex(index + 1);
                  setShowGallery(true);
                }}
              >
                <Image
                  src={image.url}
                  alt={`${title} - ${index + 1}`}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                  sizes="25vw"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>

        {/* Show all photos button - Airbnb style */}
        {allImages.length > 1 && (
          <button
            onClick={() => {
              setCurrentImageIndex(0);
              setShowGallery(true);
            }}
            className="absolute bottom-4 right-4 px-4 py-2 bg-white border border-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all flex items-center gap-2 shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Show all photos
          </button>
        )}
      </div>

      {/* Full-screen gallery modal */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent">
            <div className="flex items-center justify-between p-4">
              <button
                onClick={() => setShowGallery(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <span className="text-white text-sm font-medium">
                {currentImageIndex + 1} / {allImages.length}
              </span>
            </div>
          </div>

          {/* Image display */}
          <div className="relative w-full h-full flex items-center justify-center">
            <Image
              src={allImages[currentImageIndex]!.url}
              alt={title}
              fill
              className="object-contain"
              sizes="100vw"
            />
          </div>

          {/* Navigation */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Bottom thumbnail strip */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {allImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 relative rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImageIndex 
                      ? 'border-white scale-110' 
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <Image
                    src={image.url}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="64px"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
